import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Chip,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import { useNavigate, useParams } from "react-router-dom";
import SidebarMenu from "../AdminPanel/SidebarMenu";
import TopBar from "../AdminPanel/TopBar";
import ProductInfoTab from "./ProductDashboard/ProductInfoTab";
import ProductStatsTab from "./ProductDashboard/ProductStatsTab";
import ProductChartsTab from "./ProductDashboard/ProductChartsTab";

function getCSRFToken() {
  const name = "csrftoken";
  const cookies = document.cookie.split(";");
  for (let cookie of cookies) {
    const trimmed = cookie.trim();
    if (trimmed.startsWith(name + "=")) {
      return decodeURIComponent(trimmed.substring(name.length + 1));
    }
  }
  return null;
}

const ProductDashboard = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [open, setOpen] = useState(false);
  const [submenuOpen, setSubmenuOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [product, setProduct] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [existingImages, setExistingImages] = useState([]);
  const [newImages, setNewImages] = useState([]);

  const [formData, setFormData] = useState({
    name: "",
    barcode: "",
    codigo_auxiliar: "",
    seriado: false,
    section: "",
    category: "",
    brand: "",
    pvp1: "",
    iva_porcentaje: 15,
    iva_valor: "",
    precio_final: "",
    stock_minimo: "",
    estado: true,
    ecommerce: true,
    description: ""
  });

  const [snackbar, setSnackbar] = useState({
    open: false,
    type: "success",
    message: ""
  });

  const handleLogout = async () => {
    try {
      await fetch("/sistema/logout/", {
        method: "POST",
        credentials: "include"
      });
    } catch {}
    localStorage.clear();
    navigate("/login");
  };

  const handleSelect = (component) => {
    if (component === "AdminSection") navigate("/admin-panel/secciones");
    else if (component === "ModalInventario") setSubmenuOpen((s) => !s);
    else if (component === "AdminProduct") navigate("/admin-panel/inventario/productos");
    else if (component === "AdminVariant") navigate("/admin-panel/inventario/variantes");
    else if (component === "AdminProforma") navigate("/admin-panel/proformas");
    else if (component === "AdminClient") navigate("/admin-panel/clientes");
    else if (component === "AdminServices") navigate("/admin-panel/servicios");
    else if (component === "AdminPromotion") navigate("/admin-panel/promociones");
    else navigate("/admin-panel");
  };

  const normalizeImages = (images) => {
    if (!Array.isArray(images)) return [];

    return images
      .map((img, index) => {
        if (typeof img === "string") {
          return {
            id: "",
            url: img,
            key: `url-${index}-${img}`
          };
        }

        return {
          id: img?.id || "",
          url: img?.url || img?.image || "",
          key: `${img?.id || "img"}-${index}-${img?.url || img?.image || ""}`
        };
      })
      .filter((img) => img.url);
  };

  const loadProduct = async () => {
    setLoading(true);

    try {
      const response = await fetch(`/productos/dashboard/${id}/`, {
        credentials: "include"
      });

      const data = await response.json().catch(() => ({}));

      if (response.ok) {
        const found = data.product || null;
        setDashboardData(data);
        setProduct(found);

        if (found) {
          const pvp = Number(found.pvp1 || 0);
          const ivaPct = Number(found.iva_porcentaje || 0);
          const iva = ivaPct === 999 ? 0 : pvp * (ivaPct / 100);

          setFormData({
            name: found.name || "",
            barcode: found.barcode || "",
            codigo_auxiliar: found.codigo_auxiliar || "",
            seriado: Boolean(found.seriado),
            section: found.section_id ? String(found.section_id) : "",
            category: found.category_id ? String(found.category_id) : "",
            brand: found.brand_id ? String(found.brand_id) : "",
            pvp1: String(found.pvp1 ?? ""),
            iva_porcentaje: found.iva_porcentaje ?? 15,
            iva_valor: iva.toFixed(6),
            precio_final: (pvp + iva).toFixed(6),
            stock_minimo: found.stock_minimo ?? "",
            estado: found.estado === "activo",
            ecommerce: Boolean(found.ecommerce),
            description: found.description || ""
          });

          setExistingImages(normalizeImages(data.images || found.images || []));
          setNewImages([]);
        }
      } else {
        setDashboardData(null);
        setProduct(null);
        setExistingImages([]);
        setNewImages([]);
      }
    } catch {
      setDashboardData(null);
      setProduct(null);
      setExistingImages([]);
      setNewImages([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProduct();
  }, [id]);

  const handleSave = async () => {
    if (!product || saving) return;

    setSaving(true);

    const csrf = getCSRFToken();
    const payload = new FormData();

    payload.append("name", String(formData.name || "").trim());
    payload.append("barcode", String(formData.barcode || "").trim());
    payload.append("codigo_auxiliar", String(formData.codigo_auxiliar || "").trim());
    payload.append("seriado", formData.seriado ? "true" : "false");
    payload.append("section", String(formData.section || "").trim());
    payload.append("category", String(formData.category || "").trim());
    payload.append("brand", String(formData.brand || "").trim());
    payload.append("pvp1", String(formData.pvp1 || "0").trim());
    payload.append("iva_porcentaje", String(formData.iva_porcentaje || "15").trim());
    payload.append("stock_minimo", String(formData.stock_minimo || "").trim());
    payload.append("estado", formData.estado ? "activo" : "inactivo");
    payload.append("ecommerce", formData.ecommerce ? "true" : "false");
    payload.append("description", String(formData.description || "").trim());

    existingImages.forEach((img) => {
      if (img?.id) payload.append("keep_image_ids", String(img.id));
      if (img?.url) payload.append("keep_images", img.url);
    });

    newImages.forEach((img) => {
      if (img?.file instanceof File) {
        payload.append("images", img.file, img.file.name);
      }
    });

    try {
      const response = await fetch(`/productos/dashboard/${product.id}/actualizar/`, {
        method: "POST",
        credentials: "include",
        headers: csrf ? { "X-CSRFToken": csrf } : undefined,
        body: payload
      });

      const data = await response.json().catch(() => ({}));

      if (response.ok) {
        setSnackbar({
          open: true,
          type: "success",
          message: "Producto actualizado correctamente"
        });
        setConfirmOpen(false);
        await loadProduct();
      } else {
        setSnackbar({
          open: true,
          type: "error",
          message: data.error || "No se pudo actualizar el producto"
        });
      }
    } catch {
      setSnackbar({
        open: true,
        type: "error",
        message: "Error de conexión con el servidor"
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "#f5f6fa" }}>
      <SidebarMenu
        open={open}
        setOpen={setOpen}
        submenuOpen={submenuOpen}
        setSubmenuOpen={setSubmenuOpen}
        handleSelect={handleSelect}
        handleLogout={handleLogout}
        navigate={navigate}
      />

      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <TopBar open={open} setOpen={setOpen} setSelectedComponent={() => navigate("/admin-panel")} />

        <Box sx={{ width: "95%", mx: "auto", mt: 3, mb: 5 }}>
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/admin-panel/inventario/productos")}
            sx={{
              textTransform: "none",
              backgroundColor: "#1565C0",
              borderRadius: 2,
              px: 2.2,
              fontWeight: 700,
              mb: 2,
              boxShadow: "0 8px 20px rgba(21,101,192,0.22)"
            }}
          >
            Regresar
          </Button>

          <Paper
            sx={{
              borderRadius: 3,
              overflow: "hidden",
              boxShadow: "0 10px 30px rgba(15,23,42,0.06)",
              bgcolor: "#fff"
            }}
          >
            {loading ? (
              <Box
                sx={{
                  minHeight: 360,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}
              >
                <CircularProgress />
              </Box>
            ) : (
              <>
                <Box sx={{ p: 3, bgcolor: "#fff" }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 2,
                      flexWrap: "wrap"
                    }}
                  >
                    <Box>
                      <Typography
                        sx={{
                          fontWeight: 800,
                          fontSize: { xs: 20, md: 25 },
                          color: "#0f172a",
                          lineHeight: 1.2
                        }}
                      >
                        {product?.name || "Producto no encontrado"}
                      </Typography>

                      <Typography sx={{ color: "#64748b", fontSize: 13, mt: 0.8 }}>
                        {product?.code ? `Código: ${product.code}` : "Sin código disponible"}
                      </Typography>

                      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1.5 }}>
                        <Chip
                          label={product?.estado === "activo" ? "Activo" : "Inactivo"}
                          color={product?.estado === "activo" ? "success" : "default"}
                          size="small"
                          sx={{ fontWeight: 700, borderRadius: 2 }}
                        />

                        {product?.has_variants && (
                          <Chip
                            label={product?.variant_type ? `Variantes: ${product.variant_type}` : "Con variantes"}
                            color="info"
                            variant="outlined"
                            size="small"
                            sx={{ fontWeight: 700, borderRadius: 2 }}
                          />
                        )}
                      </Box>
                    </Box>
                  </Box>
                </Box>

                <Box
                  sx={{
                    mx: 3,
                    mt: 2,
                    backgroundColor: "#1565C0",
                    borderTopLeftRadius: "14px",
                    borderTopRightRadius: "14px",
                    overflow: "hidden"
                  }}
                >
                  <Tabs
                    value={tabValue}
                    onChange={(_e, value) => setTabValue(value)}
                    variant="scrollable"
                    scrollButtons={false}
                    sx={{
                      minHeight: "52px",
                      "& .MuiTab-root": {
                        color: "#fff",
                        textTransform: "none",
                        fontWeight: 600,
                        minHeight: "52px",
                        borderTopLeftRadius: "10px",
                        borderTopRightRadius: "10px",
                        transition: "all 0.2s ease"
                      },
                      "& .Mui-selected": {
                        backgroundColor: "#fff",
                        color: "#1565C0",
                        borderTop: "4px solid #1565C0",
                        borderLeft: "4px solid #1565C0",
                        borderRight: "4px solid #1565C0",
                        borderBottom: "none",
                        marginTop: "5px",
                        marginLeft: "10px"
                      },
                      "& .MuiTabs-indicator": {
                        display: "none"
                      }
                    }}
                  >
                    <Tab label="Administrar" />
                    <Tab label="Registros" />
                    <Tab label="Estadísticas" />
                  </Tabs>
                </Box>

                <Box sx={{ p: 3 }}>
                  {tabValue === 0 && (
                    <Box>
                      <ProductInfoTab
                        product={product || {}}
                        formData={formData}
                        setFormData={setFormData}
                        existingImages={existingImages}
                        setExistingImages={setExistingImages}
                        newImages={newImages}
                        setNewImages={setNewImages}
                      />

                      <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                        <Button
                          variant="contained"
                          startIcon={<SaveOutlinedIcon />}
                          onClick={() => setConfirmOpen(true)}
                          disabled={!product || saving || !formData.section || !formData.category || !formData.brand}
                          sx={{
                            textTransform: "none",
                            backgroundColor: "#1565C0",
                            borderRadius: 2,
                            px: 2.5,
                            fontWeight: 700,
                            boxShadow: "0 8px 20px rgba(21,101,192,0.22)"
                          }}
                        >
                          Actualizar producto
                        </Button>
                      </Box>
                    </Box>
                  )}

                  {tabValue === 1 && <ProductStatsTab product={product} dashboardData={dashboardData} />}
                  {tabValue === 2 && <ProductChartsTab product={product} dashboardData={dashboardData} />}
                </Box>
              </>
            )}
          </Paper>
        </Box>
      </Box>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Guardar cambios</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: "#475569" }}>
            ¿Estás seguro de actualizar la información de este producto?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmOpen(false)} variant="outlined">
            Cancelar
          </Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}>
            {saving ? "Guardando..." : "Sí, guardar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.type}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProductDashboard;